import {
  ApplicationConfig,
  importProvidersFrom,
  isDevMode,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withInMemoryScrolling,
  type InMemoryScrollingFeature,
  type InMemoryScrollingOptions,
  UrlSerializer
} from '@angular/router';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { routes } from './app.routes';
import { provideEffects } from '@ngrx/effects';
import { rootReducer } from './store';
import { DatePipe, DecimalPipe } from '@angular/common';
import { AuthenticationEffects } from './store/authentication/authentication.effects';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { FakeBackendProvider } from './core/helpers/fake-backend';
import { CalendarEffects } from './store/calendar/calendar.effects';
import { KanbanEffects } from './store/kanban/kanban.effects';
import { UserEffects } from './store/users/users.effects';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { provideTranslateService, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideMomentDateAdapter, MAT_MOMENT_DATE_FORMATS } from '@angular/material-moment-adapter';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { CacheBusterUrlSerializer } from './core/service/cache-buster-url-serializer';
// scroll
const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
}

const languageSetter = localStorage.getItem("lang") ?? "es";
const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (http: HttpClient) =>
  new TranslateHttpLoader(http, './i18n/', '.json');

const inMemoryScrollingFeatures: InMemoryScrollingFeature =
  withInMemoryScrolling(scrollConfig)

export const appConfig: ApplicationConfig = {
  providers: [
    FakeBackendProvider,
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: languageSetter === 'es' ? "es" : languageSetter
    }),
    DatePipe,
    DecimalPipe,
    provideZoneChangeDetection({
      eventCoalescing: false,
      runCoalescing: false,
      ignoreChangesOutsideZone: true,
    }),
    provideRouter(routes, inMemoryScrollingFeatures),
    { provide: UrlSerializer, useClass: CacheBusterUrlSerializer },
    provideStore(rootReducer),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    provideEffects(UserEffects),
    provideEffects(CalendarEffects),
    provideEffects(AuthenticationEffects, KanbanEffects),
    provideMomentDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    // ✅ Register interceptor here
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },


    // ✅ Http client with interceptor support
    provideHttpClient(
      withFetch(),
      withInterceptorsFromDi() // This pulls in the interceptor above
    ), provideAnimationsAsync(),
  ],
};
